// src/components/layout/DashboardLayout.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useChat } from '../../context/ChatContext';
import ChatNavbarIcon from './ChatNavbarIcon';
import ChatDrawer from '../chat/ChatDrawer';
import ChatWindow from '../chat/ChatWindow'; // ✅ Rendered ONCE here
import {
  AppBar, Toolbar, Typography, Button, Drawer, List, ListItem, ListItemIcon,
  ListItemText, Box, Divider, CssBaseline, IconButton, Menu, MenuItem,
  CircularProgress, useMediaQuery, useTheme, Avatar, Tooltip,
} from "@mui/material";
import {
  Dashboard as DashboardIcon, Work as ProjectIcon, Checklist as TaskIcon,
  People as TeamIcon, CalendarMonth as CalendarMonthIcon, Inventory as PackageIcon,
  ExitToApp as LogoutIcon, Menu as MenuIcon, AccountCircle as AccountCircleIcon,
  MonetizationOn as MonetizationOnIcon, TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon, Label as LabelIcon, ViewKanban as ViewKanbanIcon,
  Business as BusinessIcon, Domain as DomainIcon,
} from "@mui/icons-material";
import NotificationBell from "../NotificationBell";
import syncroLogo from '../../assets/syncro_logo_color.svg';
import syncroIcon from '../../assets/syncro_icon.svg';

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED_WIDTH = 72;
const HOVER_DELAY = 120;

const DashboardLayout = ({ children, noSidebarMargin = false }) => {
  const { logout, company, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const collapsedRef = useRef(sidebarCollapsed);
  const hoverTimeoutRef = useRef(null);
  const { 
    openChats, // ✅ Extract openChats
    openChat, closeChat, toggleMinimize, focusChat // ✅ Extract actions
  } = useChat();

  useEffect(() => { collapsedRef.current = sidebarCollapsed; }, [sidebarCollapsed]);
  useEffect(() => { return () => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); }; }, []);

  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    const baseUrl = process.env.REACT_APP_AVATAR_URL?.replace(/\/+$/, '') || 'http://localhost:8000/storage';
    return `${baseUrl}/${avatarPath.replace(/^\/+/, '')}?t=${Date.now()}`;
  };

  useEffect(() => { if (isMobile) setMobileOpen(false); }, [location, isMobile]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleLogoutClick = (event) => setAnchorEl(event.currentTarget);
  const handleLogoutClose = () => setAnchorEl(null);
  const handleLogoutConfirm = () => { logout(); navigate("/login"); handleLogoutClose(); };

  const handleDrawerMouseEnter = useCallback(() => {
    if (isMobile) return;
    clearTimeout(hoverTimeoutRef.current);
    if (collapsedRef.current) hoverTimeoutRef.current = setTimeout(() => setSidebarCollapsed(false), HOVER_DELAY);
  }, [isMobile]);

  const handleDrawerMouseLeave = useCallback(() => {
    if (isMobile) return;
    clearTimeout(hoverTimeoutRef.current);
    if (!collapsedRef.current) hoverTimeoutRef.current = setTimeout(() => setSidebarCollapsed(true), HOVER_DELAY);
  }, [isMobile]);

  const menuItems = useMemo(() => {
    const items = [];
    const isClient = !!user?.client_id;
    if (isClient) {
      items.push({ text: "Projects", icon: <ProjectIcon />, path: "/projects", tooltip: "View and manage your projects" });
    } else {
      items.push({ text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard", tooltip: "Overview of all activities" });
      if (user?.hasPermission?.("team.view.page")) items.push({ text: "Team", icon: <TeamIcon />, path: "/team", tooltip: "Manage team members" });
      if (user?.hasPermission?.("client.view.page")) items.push({ text: "Clients", icon: <BusinessIcon />, path: "/clients", tooltip: "Manage clients" });
      if (user?.hasPermission?.("status.view.page")) items.push({ text: "Status", icon: <LabelIcon />, path: "/statuses", tooltip: "Manage status labels" });
      if (user?.hasPermission?.("project.view.page")) items.push({ text: "Projects", icon: <ProjectIcon />, path: "/projects", tooltip: "View and manage projects" });
      if (user?.hasPermission?.("project.board.view")) items.push({ text: "Board", icon: <ViewKanbanIcon />, path: "/board", tooltip: "Kanban board view" });
      if (user?.hasPermission?.("task.view.page")) items.push({ text: "Tasks", icon: <TaskIcon />, path: "/tasks", tooltip: "Manage tasks" });
      if (user?.hasPermission?.("calendar.view")) items.push({ text: "Calendar", icon: <CalendarMonthIcon />, path: "/calendar", tooltip: "View calendar" });
      if (user?.hasPermission?.("package.view")) items.push({ text: "Packages", icon: <PackageIcon />, path: "/packages", tooltip: "Manage packages" });
      if (user?.hasPermission?.("billing.manage")) items.push({ text: "Billing", icon: <MonetizationOnIcon />, path: "/billing", tooltip: "Manage billing and invoices" });
      if (user?.hasPermission?.("kpi.view")) items.push({ text: "KPI", icon: <TrendingUpIcon />, path: "/kpi-approvals", tooltip: "View KPI approvals" });
      if (user?.hasPermission?.("documentation.view")) items.push({ text: "Workflow Guide", icon: <DescriptionIcon />, path: "/documentation", tooltip: "View workflow documentation" });
    }
    return items;
  }, [user]);

  if (loading || !user) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", bgcolor: "#f5f7fa" }}>
        <CircularProgress sx={{ color: "#667eea" }} />
      </Box>
    );
  }

  const drawerWidth = sidebarCollapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;
  const gradientPrimary = "linear-gradient(135deg, #667eea 0%, #e91e63 100%)";
  const activeGradient = sidebarCollapsed
    ? "linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(233, 30, 99, 0.15) 100%)"
    : "linear-gradient(90deg, rgba(102, 126, 234, 0.1) 0%, rgba(233, 30, 99, 0.1) 100%)";

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#ffffff", borderRight: "1px solid", borderColor: "rgba(0,0,0,0.08)" }}>
      <Toolbar sx={{ minHeight: 60, px: sidebarCollapsed ? 1.5 : 2.5, display: "flex", alignItems: "center", justifyContent: sidebarCollapsed ? "center" : "flex-start" }}>
        {sidebarCollapsed && <Box sx={{ width: 40, height: 40 }} />}
      </Toolbar>
      <List sx={{ py: 2, px: 1.5, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          const menuItemContent = (
            <ListItem button onClick={() => navigate(item.path)} sx={{
              borderRadius: 3, mb: 0.5, px: sidebarCollapsed ? 1.5 : 2.5, py: 1.5,
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              background: isActive ? activeGradient : "transparent",
              color: isActive ? "#667eea" : "#475569", position: "relative", overflow: "hidden",
              "&::before": isActive && !sidebarCollapsed ? { content: '""', position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", width: 4, height: "60%", background: gradientPrimary, borderRadius: "0 4px 4px 0" } : {},
              "&:hover": { background: isActive ? activeGradient : "rgba(102, 126, 234, 0.06)", color: "#667eea", "& .MuiListItemIcon-root": { color: "#667eea", transform: "scale(1.1)" }, transform: sidebarCollapsed ? "translateX(4px)" : "translateX(2px)" },
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)", cursor: "pointer"
            }}>
              <Tooltip title={sidebarCollapsed ? item.tooltip : ""} placement="right" disableHoverListener={!sidebarCollapsed} arrow>
                <ListItemIcon sx={{ minWidth: 0, mr: sidebarCollapsed ? 0 : 2, justifyContent: "center", color: isActive ? "#667eea" : "#94a3b8", transition: "all 0.2s ease" }}>
                  {React.cloneElement(item.icon, { fontSize: "medium", sx: { color: "inherit", transition: "all 0.2s ease" } })}
                </ListItemIcon>
              </Tooltip>
              {!sidebarCollapsed && (
                <ListItemText primary={item.text} sx={{ "& .MuiTypography-root": { fontSize: "0.875rem", fontWeight: isActive ? 600 : 500, color: "inherit", letterSpacing: "0.3px" } }} />
              )}
            </ListItem>
          );
          return sidebarCollapsed ? (<Tooltip key={item.text} title={item.tooltip} placement="right" arrow>{menuItemContent}</Tooltip>) : menuItemContent;
        })}
      </List>
      <Box sx={{ px: sidebarCollapsed ? 1.5 : 2.5, py: 2, textAlign: "center", fontSize: "0.72rem", color: "#94a3b8", borderTop: "1px solid", borderColor: "rgba(0,0,0,0.06)", mt: "auto" }}>
        {sidebarCollapsed ? (
          <>
            <Box component="img" src={syncroIcon} alt="Syncro" sx={{ width: 24, height: 24, objectFit: "contain", opacity: 0.9, "&:hover": { opacity: 1 }, transition: "opacity 0.2s ease", cursor: "pointer", mb: 0.5 }} onClick={() => navigate("/dashboard")} />
            <Box component="span" sx={{ color: "#667eea", fontWeight: 700, cursor: "pointer", "&:hover": { color: "#e91e63" } }} onClick={() => window.open("https://softsolutionmedia.com/", "_blank", "noopener,noreferrer")}>SSM</Box>
          </>
        ) : (
          <>
            <Box component="img" src={syncroLogo} alt="Syncro" sx={{ width: "auto", maxWidth: 100, height: 28, objectFit: "contain", opacity: 0.9, "&:hover": { opacity: 1 }, transition: "opacity 0.2s ease", cursor: "pointer", mb: 0.5 }} onClick={() => navigate("/dashboard")} />
            <Box>
              © {new Date().getFullYear()}{" "}
              <Box component="span" sx={{ color: "#667eea", fontWeight: 600, cursor: "pointer", "&:hover": { color: "#e91e63" }, transition: "color 0.2s ease" }} onClick={() => window.open("https://softsolutionmedia.com/", "_blank", "noopener,noreferrer")}>Soft Solution Media</Box>
              <br />All rights reserved.
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <CssBaseline />
      <AppBar position="fixed" elevation={0} sx={{ bgcolor: "#ffffff", borderBottom: "1px solid", borderColor: "rgba(0,0,0,0.08)", zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: 64, px: { xs: 2, sm: 3 }, display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {isMobile && (
              <IconButton edge="start" onClick={handleDrawerToggle} sx={{ mr: 1, color: "#1e293b", bgcolor: "rgba(102, 126, 234, 0.08)", "&:hover": { bgcolor: "rgba(102, 126, 234, 0.15)", color: "#667eea" }, transition: "all 0.2s ease" }} aria-label="Open menu">
                <MenuIcon sx={{ fontSize: 24 }} />
              </IconButton>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DomainIcon sx={{ color: "#667eea", fontSize: 24 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.125rem", lineHeight: 1.2 }}>{company?.company_name || "Syncro"}</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1.5 } }}>
            <ChatNavbarIcon />
            <NotificationBell tooltipTitle="Notifications" badgeColor="error" badgeGradient="linear-gradient(135deg, #e91e63 0%, #f093fb 100%)" sx={{ color: "#475569", "&:hover": { color: "#667eea" } }} />
            <Tooltip title={`${user?.name}\nView Profile`} placement="bottom" arrow>
              <Button color="inherit" onClick={handleLogoutClick} sx={{ textTransform: "none", px: 1.5, py: 0.75, borderRadius: 3, background: "rgba(102, 126, 234, 0.06)", "&:hover": { bgcolor: "rgba(102, 126, 234, 0.12)", transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)" }, transition: "all 0.2s ease" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, fontSize: "0.9rem", background: gradientPrimary, color: "#fff", boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)" }} src={getAvatarUrl(user?.avatar)} onError={(e) => { e.target.onerror = null; e.target.src = null; }}>
                    {!user?.avatar ? user?.name?.charAt(0)?.toUpperCase() : null}
                  </Avatar>
                  <Box sx={{ display: { xs: "none", md: "block" }, textAlign: "left" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.875rem" }}>{user?.name}</Typography>
                    <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", fontSize: "0.75rem" }}>
                      {user?.role?.name || user?.teamMembership?.role?.name || user?.membership?.role?.name || user?.current_role?.name || "Member"}
                    </Typography>
                  </Box>
                </Box>
              </Button>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleLogoutClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} sx={{ mt: 1.5 }} PaperProps={{ elevation: 8, sx: { borderRadius: 3, mt: 1, minWidth: 200, border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 10px 40px rgba(0,0,0,0.12)", "& .MuiMenuItem-root": { borderRadius: 2, mx: 1, mb: 0.5, "&:last-of-type": { mb: 0 } } } }}>
              <MenuItem onClick={() => { navigate('/profile'); handleLogoutClose(); }} sx={{ gap: 1.5, py: 1.5 }}>
                <AccountCircleIcon sx={{ color: "#667eea", fontSize: 20 }} />
                <Box><Typography variant="body2" fontWeight={600}>Profile Settings</Typography><Typography variant="caption" color="text.secondary">Manage your account</Typography></Box>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleLogoutConfirm} sx={{ gap: 1.5, py: 1.5, color: "#e91e63" }}>
                <LogoutIcon sx={{ fontSize: 20 }} />
                <Box><Typography variant="body2" fontWeight={600}>Logout</Typography><Typography variant="caption" color="text.secondary">Sign out of your account</Typography></Box>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer variant={isMobile ? "temporary" : "persistent"} open={isMobile ? mobileOpen : true} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} PaperProps={{
        onMouseEnter: handleDrawerMouseEnter, onMouseLeave: handleDrawerMouseLeave,
        sx: { width: drawerWidth, transition: theme.transitions.create("width", { easing: theme.transitions.easing.sharp, duration: 200 }), overflowX: "hidden", borderRight: "none", boxShadow: isMobile ? "4px 0 20px rgba(0,0,0,0.1)" : "0 0 20px rgba(0,0,0,0.05)", ...(isMobile && { zIndex: (t) => t.zIndex.drawer + 2 }) }
      }}>
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, mt: "64px", ml: isMobile ? 0 : (noSidebarMargin ? 0 : `${drawerWidth}px`), transition: theme.transitions.create("margin", { easing: theme.transitions.easing.easeInOut, duration: 200 }), minHeight: "calc(100vh - 64px)", bgcolor: "#f5f7fa" }}>
        {children}
      </Box>

      {/* ✅ Chat Components - Ensure ChatWindow is ONLY here */}
      <ChatDrawer />
      {openChats.map((chat) => (
        <ChatWindow
          key={chat.id}
          conversationId={chat.id}
          isMinimized={chat.minimized}
          onClose={() => closeChat(chat.id)}
          onMinimize={() => toggleMinimize(chat.id)}
          onFocus={() => focusChat(chat.id)}
          zIndex={chat.zIndex}
        />
      ))}
    </Box>
  );
};

export default React.memo(DashboardLayout);